import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { DollarSign, TrendingUp, Calendar, CheckCircle2 } from "lucide-react";

type Booking = {
  id: string;
  start_at: string;
  end_at: string;
  total_amount: number;
  status: string;
  created_at: string;
  listings: {
    title: string;
  } | null;
};

interface EarningsOverviewProps {
  userId: string;
}

export default function EarningsOverview({ userId }: EarningsOverviewProps) {
  // Hent alle bookinger hvor brugeren er ejer
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["earnings", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(`
          *,
          listings!inner(title, owner_id)
        `)
        .eq("listings.owner_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Booking[];
    },
  });

  // Beregn statistik - Platform fee er 15%, udlejer får 85%
  const PLATFORM_FEE = 0.15;
  const HOST_PERCENTAGE = 1 - PLATFORM_FEE; // 0.85 (85%)

  const confirmedOrCompleted = bookings.filter(b => 
    b.status === "confirmed" || b.status === "completed"
  );

  const stats = {
    // Brutto (hvad lejer har betalt)
    totalEarningsGross: confirmedOrCompleted
      .reduce((sum, b) => sum + b.total_amount, 0) / 100,
    
    // Netto (hvad udlejer får efter 15% platform fee)
    totalEarningsNet: confirmedOrCompleted
      .reduce((sum, b) => sum + b.total_amount * HOST_PERCENTAGE, 0) / 100,
    
    pendingEarnings: bookings
      .filter(b => b.status === "pending")
      .reduce((sum, b) => sum + b.total_amount * HOST_PERCENTAGE, 0) / 100,
    
    completedBookings: confirmedOrCompleted.length,
    
    thisMonthEarnings: bookings
      .filter(b => {
        const bookingDate = new Date(b.created_at);
        const now = new Date();
        return bookingDate.getMonth() === now.getMonth() &&
               bookingDate.getFullYear() === now.getFullYear() &&
               (b.status === "confirmed" || b.status === "completed");
      })
      .reduce((sum, b) => sum + b.total_amount * HOST_PERCENTAGE, 0) / 100,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
      case "completed":
        return "default";
      case "pending":
        return "secondary";
      case "cancelled":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "confirmed":
        return "Bekræftet";
      case "completed":
        return "Gennemført";
      case "pending":
        return "Afventer";
      case "cancelled":
        return "Annulleret";
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Din indtjening</p>
                <p className="text-2xl font-bold">{stats.totalEarningsNet.toFixed(0)} kr</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Efter 15% platform fee
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-warning/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Denne måned</p>
                <p className="text-2xl font-bold">{stats.thisMonthEarnings.toFixed(0)} kr</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/10 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gennemførte</p>
                <p className="text-2xl font-bold">{stats.completedBookings}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Calendar className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Afventende</p>
                <p className="text-2xl font-bold">{stats.pendingEarnings.toFixed(0)} kr</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Udbetalingsinfo */}
      <Card className="bg-muted/50 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg mt-0.5">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">Udbetaling</p>
              <p className="text-sm text-muted-foreground">
                Din indtjening overføres til din bankkonto via Stripe 7-10 hverdage efter endt booking.
                Platformen forbeholder sig 15% i service-gebyr af hver booking.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaktionshistorik */}
      <Card>
        <CardHeader>
          <CardTitle>Transaktionshistorik</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Henter transaktioner...</p>
          ) : bookings.length === 0 ? (
            <p className="text-muted-foreground">
              Ingen transaktioner endnu. Når du får bookinger, vil de fremgå her.
            </p>
          ) : (
            <div className="space-y-3">
              {bookings.map((booking, idx) => (
                <div key={booking.id}>
                  <div className="flex items-center justify-between py-3">
                    <div className="space-y-1 flex-1">
                      <div className="font-medium">
                        {booking.listings?.title || "Ukendt plads"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(booking.start_at), "d. MMM yyyy", { locale: da })}
                        {" - "}
                        {format(new Date(booking.end_at), "d. MMM yyyy", { locale: da })}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant={getStatusColor(booking.status)}>
                        {getStatusText(booking.status)}
                      </Badge>
                      <div className="text-right min-w-[100px]">
                        <div className="font-semibold">
                          {(booking.total_amount * HOST_PERCENTAGE / 100).toFixed(2)} kr
                        </div>
                        <div className="text-xs text-muted-foreground">
                          (brutto: {(booking.total_amount / 100).toFixed(0)} kr)
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Dato booket: {format(new Date(booking.created_at), "d/M", { locale: da })}
                        </div>
                      </div>
                    </div>
                  </div>
                  {idx < bookings.length - 1 && <Separator />}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Udbetalingsinfo */}
      <Card>
        <CardHeader>
          <CardTitle>Udbetaling</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Indtjening udbetales automatisk via Stripe Connect efter gennemført booking.
          </p>
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm">
              <strong>Næste udbetaling:</strong> Automatisk efter booking gennemførelse
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
